import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChevronsDownUp, ChevronsUpDown, Loader2 } from "lucide-react";
import SearchBar from "@/components/SearchBar";
import PullScreen from "./PullScreen";
import { EmptyDay, TicketCard, WeekStrip, dayHeading, fmtDay, usePullQueue } from "./PullQueue";

/** WH-DESK-1 — the mobile Warehouse at desktop width: filter strip, the day's queue on the left, the pull screen on the right. */
export default function PullDesk({ week, day, pull, line = "", onWeek, onDay, onJump, onPull, actor, onChanged }) {
  const { data, loading, selected: sel, reload } = usePullQueue(week, day, onDay);
  // the panes fill what is left of the viewport so the Done bar always sits at the pane bottom
  const panesRef = useRef(null);
  const [paneH, setPaneH] = useState("60vh");
  useLayoutEffect(() => {
    const fit = () => { const top = panesRef.current?.getBoundingClientRect().top || 0; setPaneH(`${Math.max(420, window.innerHeight - top - 16)}px`); };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [data, pull]);
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState(new Set());

  const tickets = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (sel?.tickets || []).filter((c) => (!needle || `${c.job_number} ${c.project_name} ${c.ticket_number} ${c.scopes.join(" ")}`.toLowerCase().includes(needle)));
  }, [sel, q]);
  const groups = useMemo(() => {          // always by job
    const m = new Map();
    tickets.forEach((c) => { const k = c.project_id; if (!m.has(k)) m.set(k, { key: k, label: `${c.job_number || "—"} · ${c.project_name}`, tickets: [] }); m.get(k).tickets.push(c); });
    return [...m.values()];
  }, [tickets]);
  const openCount = tickets.filter((c) => c.wh_status !== "Delivered").length;
  const jobCount = new Set(tickets.map((c) => c.project_id)).size;
  const allCollapsed = groups.length > 0 && groups.every((g) => collapsed.has(g.key));
  const idx = tickets.findIndex((c) => c.id === pull);
  const step = (d) => { const n = tickets[idx + d]; if (n) onPull(n.id); };
  const changed = () => { reload(); onChanged?.(); };

  return (
    <div className="space-y-3" data-testid="pull-desk">
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="pull-desk-strip">
        <button type="button" onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(groups.map((g) => g.key)))} data-testid="pull-desk-expand-all"
                title={allCollapsed ? "Expand all jobs" : "Collapse all jobs"}
                className="grid h-7 w-7 place-items-center rounded border border-border text-foreground hover:bg-muted disabled:opacity-40">
          {allCollapsed ? <ChevronsUpDown className="h-3.5 w-3.5" /> : <ChevronsDownUp className="h-3.5 w-3.5" />}
        </button>
        <SearchBar value={q} onChange={setQ} placeholder="Search job, ticket, scope…" testId="pull-desk-search" width="w-64" />
        <WeekStrip data={data} day={day} onDay={onDay} onWeek={onWeek} loading={loading} inline />
        <span className="ml-auto text-xs text-muted-foreground" data-testid="pull-desk-summary">{tickets.length} ticket{tickets.length === 1 ? "" : "s"} · {openCount} open · {jobCount} job{jobCount === 1 ? "" : "s"}</span>
      </div>

      <div ref={panesRef} className="grid grid-cols-[35fr_65fr] gap-3 items-stretch" style={{ height: paneH, minHeight: 420 }}>
        <aside className="min-h-0 overflow-auto rounded-lg border border-border bg-muted/30 p-2 space-y-2" data-testid="pull-desk-queue">
          {!data ? <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
            <>
              <div className="text-xs text-muted-foreground px-1" data-testid="pull-desk-day-heading">{dayHeading(sel)}{data.week_end < data.today ? " · history, read-only" : ""}</div>
              {tickets.length === 0 && <EmptyDay />}
              {groups.map((g) => (
                <div key={g.key || "_all"} className="space-y-2" data-testid={`pull-desk-group-${g.key || "all"}`}>
                  {g.label && (
                    <button type="button" onClick={() => setCollapsed((s) => { const n = new Set(s); n.has(g.key) ? n.delete(g.key) : n.add(g.key); return n; })}
                            className="w-full text-left px-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1" data-testid={`pull-desk-group-toggle-${g.key}`}>
                      {collapsed.has(g.key) ? <ChevronsUpDown className="h-3 w-3" /> : <ChevronsDownUp className="h-3 w-3" />}{g.label} · {g.tickets.length}
                    </button>
                  )}
                  {!collapsed.has(g.key) && g.tickets.map((c) => <TicketCard key={c.id} c={c} compact selected={c.id === pull} onOpen={(x) => onPull(x.id)} showDay={!!sel?.multi} />)}
                </div>
              ))}
            </>
          )}
        </aside>
        <section className="min-h-0 rounded-lg border border-border overflow-hidden bg-background" data-testid="pull-desk-pane">
          {pull ? (
            <PullScreen key={pull} ticketId={pull} actor={actor} embedded highlightKey={line} onChanged={changed}
                        onPrevTicket={idx > 0 ? () => step(-1) : undefined} onNextTicket={idx >= 0 && idx < tickets.length - 1 ? () => step(1) : undefined} />
          ) : (
            <div className="h-full flex flex-col items-center justify-center gap-1 text-sm text-muted-foreground p-8 text-center" data-testid="pull-desk-pane-empty"><span>Pick a ticket on the left to start pulling.</span><span className="text-xs">← → move between tickets once one is open.</span></div>
          )}
        </section>
      </div>
    </div>
  );
}
